import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Select } from 'antd'
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import CommonTablePage from '../../../components/common/common-table/common-table-page'
import OperationLogColumnModel from './operation-log-column-model'
import { ApiRequest } from '../../../services/api/api'
import { useDocumentTitle } from '../../../services/title/useDocumentTitle'
import moment from 'moment'

const { RangePicker } = DatePicker
const { Option } = Select

// 后端 OperType 枚举
const OPER_TYPE_OPTIONS = [
  'Relocating',
  'Picking',
  'Returning',
  'Inbound',
  'Production',
  'ManualDelete',
  'ManualAdd',
]

const makeUrl = (startDate: any, endDate: any, productId?: any, operType?: string) => {
  let url = 'OperationLog/GetLogs'
  const params: string[] = []
  if (startDate) params.push('start=' + moment(startDate).format('YYYY-MM-DD'))
  if (endDate) params.push('end=' + moment(endDate).format('YYYY-MM-DD'))
  if (productId != null && productId !== '') params.push('productId=' + productId)
  if (operType && operType.trim()) params.push('operType=' + encodeURIComponent(operType.trim()))
  if (params.length) url += '?' + params.join('&')
  return url
}

const OperationLogPage = () => {
  useDocumentTitle('Operation Log')

  // 默认查最近 1 个月
  const defaultStart = moment().add(-1, 'months')
  const defaultEnd = moment()

  const [startDate, setStartDate] = useState<any>(defaultStart)
  const [endDate, setEndDate] = useState<any>(defaultEnd)
  const [productId, setProductId] = useState<any>()
  const [operType, setOperType] = useState<string | undefined>()

  const [url, setUrl] = useState<string>(makeUrl(defaultStart, defaultEnd))
  const [triggerResetData, setTriggerResetData] = useState<any>(false)
  const [employeeOptions, setEmployeeOptions] = useState<any[]>([])
  const [productOptions, setProductOptions] = useState<any[]>([])
  // CommonTablePage 只在挂载时拷贝一次 column，
  // 必须等 lookup 数据加载完成后再挂载表格，否则 Product/Employee 列只能显示 ID
  const [optionsReady, setOptionsReady] = useState(false)

  useEffect(() => {
    const fetchEmployees = ApiRequest({
      url: 'Employee/GetAllEmployee',
      method: 'get',
      isShowSpinner: false,
    }).then((res: any) => {
      setEmployeeOptions(res.data?.data || [])
    }).catch(() => setEmployeeOptions([]))

    const fetchProducts = ApiRequest({
      url: 'Product/GetAllProduct?withOutStock=true',
      method: 'get',
      isShowSpinner: false,
    }).then((res: any) => {
      setProductOptions(res.data?.data || [])
    }).catch(() => setProductOptions([]))

    Promise.all([fetchEmployees, fetchProducts]).finally(() => setOptionsReady(true))
  }, [])

  // 员工 ID → { firstName, fullName }（列显示 First name，悬停显示全名）
  const employeeLookup = employeeOptions.reduce((map: any, employee: any) => {
    const firstName = employee.firstName || ''
    const lastName = employee.lastName || ''
    map[employee.employeeId] = {
      firstName: firstName || employee.userName || employee.employeeId,
      fullName: `${firstName} ${lastName}`.trim() || employee.userName || employee.employeeId,
    }
    return map
  }, {})
  console.log('[OperationLog] employee lookup size:', Object.keys(employeeLookup).length)

  // 产品 ID → 产品对象（列显示编码，悬停显示名称）
  const productLookup = productOptions.reduce((map: any, product: any) => {
    map[product.productId] = product
    return map
  }, {})

  // 日期变化即查询（与其他报表页一致）
  const onChangeDateRange = (value: any) => {
    setStartDate(value ? value[0] : null)
    setEndDate(value ? value[1] : null)
    setUrl(makeUrl(value ? value[0] : null, value ? value[1] : null, productId, operType))
    setTriggerResetData(Date.now())
  }

  // ProductId / OperType 点 Search 生效
  const onSearch = () => {
    setUrl(makeUrl(startDate, endDate, productId, operType))
    setTriggerResetData(Date.now())
  }

  const onReset = () => {
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
    setProductId(undefined)
    setOperType(undefined)
    setUrl(makeUrl(defaultStart, defaultEnd))
    setTriggerResetData(Date.now())
  }

  const commonTablePageProps: any = {
    getAllUrl: url,
    title: 'Operation Log',
    column: OperationLogColumnModel(employeeLookup, productLookup),
    triggerResetData: triggerResetData,
    isNotDeletable: true,
    isNotEditable: true,
    isNotAddable: true,
    defaultPageSize: 20,
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
        <RangePicker value={[startDate, endDate]} onChange={onChangeDateRange} />
        <Select
          placeholder="Product"
          showSearch
          allowClear
          optionFilterProp="children"
          value={productId}
          onChange={(v: any) => setProductId(v)}
          style={{ width: 300 }}
        >
          {productOptions.map((product: any) => (
            <Option key={product.productId} value={product.productId}>
              {`${product.productCode || ''} - ${product.productName || ''}`}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Oper Type"
          showSearch
          allowClear
          optionFilterProp="children"
          value={operType}
          onChange={(v: any) => setOperType(v)}
          style={{ width: 160 }}
        >
          {OPER_TYPE_OPTIONS.map((type: string) => (
            <Option key={type} value={type}>{type}</Option>
          ))}
        </Select>
        <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>Search</Button>
        <Button icon={<ReloadOutlined />} onClick={onReset}>Reset</Button>
      </div>
      {optionsReady ? <CommonTablePage {...commonTablePageProps} /> : null}
    </div>
  )
}

export default OperationLogPage
